<template>
  <div class="detail-container">
    <h1 class="page-title">臨時作業情報詳細</h1>
    <div v-if="temporaryWork">
      <table class="table table-bordered">
        <tr>
          <th>請求ID</th>
          <td>{{ temporaryWork.billing_no }}</td>
        </tr>
        <tr>
          <th>見積No.</th>
          <td>{{ temporaryWork.quotion_no }}</td>
        </tr>
        <tr>
          <th>建物</th>
          <td>{{ temporaryWork.m_building?.building_nm }}</td>
        </tr>
        <tr>
          <th>区分</th>
          <td>{{ temporaryWork.m_business_classification?.business_classification_nm }}</td>
        </tr>
        <tr>
          <th>業務内容</th>
          <td class="multiline-text">{{ temporaryWork.work_content }}</td>
        </tr>
        <tr>
          <th>内訳</th>
          <td class="multiline-text">{{ temporaryWork.breakdown }}</td>
        </tr>
        <tr>
          <th>顧客名(請求先)</th>
          <td class="multiline-text">{{ temporaryWork.m_customer_representative?.m_customer?.customer_nm }}</td>
        </tr>
        <tr>
          <th>顧客(担当者)</th>
          <td>
            {{ temporaryWork.m_customer_representative?.in_charge_last_nm }} 
            {{ temporaryWork.m_customer_representative?.in_charge_first_nm }}
          </td>
        </tr>
        <tr>
          <th>受注日</th>
          <td>{{ formatYearMonthDay(temporaryWork.order_dt) }}</td>
        </tr>
        <tr>
          <th>受注金額(税抜)</th>
          <td>{{ formatCurrency(temporaryWork.order_amount) }}</td>
        </tr>
        <tr>
          <th>発注業者情報</th>
          <td>
            <!-- 自社で対応 -->
            <template v-if="temporaryWork.is_in_house">自社で作業</template>

            <!-- 業者がいる場合 -->
            <table
              v-else-if="temporaryWork.t_temporary_work_vendors?.length != 0"
              class="vendor-info-table table table-bordered"
            >
              <thead>
                <tr>
                  <th>発注業者</th>
                  <th>発注金額(税抜)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="vendor in temporaryWork.t_temporary_work_vendors" :key="vendor.id">
                  <td class="text-nowrap">{{ vendor.m_vendor.vendor_nm }}</td>
                  <td class="text-nowrap text-end">{{ formatCurrency(vendor.request_amount) }}</td>
                </tr>
              </tbody>
            </table>

            <!-- 業者もいない場合（データが不正な場合など） -->
            <span v-else>発注業者情報なし</span>
          </td>
        </tr>
        <tr>
          <th>業者請求確認(総務チェック)</th>
          <td>
            <span v-if="temporaryWork.confirm_vendor_billing">確認済み</span>
          </td>
        </tr>
        <tr>
          <th>業者請求確認日(総務チェック日)</th>
          <td>{{ formatYearMonthDay(temporaryWork.vendor_billing_confirm_dt) }}</td>
        </tr>
        <tr>
          <th>粗利(%)</th>
          <td>{{ temporaryWork.gross_profit_margin }}%</td>
        </tr>
        <tr>
          <th>作業日</th>
          <td>{{ formatYearMonthDay(temporaryWork.work_dt) }}</td>
        </tr>
        <tr>
          <th>当社担当者</th>
          <td>{{ temporaryWork.user?.name }}</td>
        </tr>
        <tr>
          <th>請求年月</th>
          <td>
            {{ temporaryWork.billing_year }}年{{ temporaryWork.billing_month }}月
          </td>
        </tr>
        <tr>
          <th>請求発行依頼</th>
          <td>{{ temporaryWork.billing_request }}</td>
        </tr>
        <tr>
          <th>請求確認(総務チェック)</th>
          <td>
            <span v-if="temporaryWork.confirm_billing"></span>確認済み
          </td>
        </tr>
        <tr>
          <th>請求確認日(総務チェック日)</th>
          <td>{{ formatYearMonthDay(temporaryWork.confirm_billing_dt) }}</td>
        </tr>
      </table>

      <div class="btn-area">
        <router-link
          :to="{
            name: 'TemporaryWorkList',
            query: route.query
          }"
        >
          <button type="button" class="btn btn-primary me-2">臨時作業一覧</button>
        </router-link>

        <router-link
          :to="{ 
            name: 'TemporaryWorkEdit',
            params: { id: temporaryWork.id },
            query: getSearchQuery()
          }"
        >
          <button type="button" class="btn btn-warning">編集</button>
        </router-link>
      </div>
    </div>
    <!-- その他の詳細情報 -->
    <div v-else>
      臨時作業情報情報を読み込み中...
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { getTemporaryWork } from '@/api/axios';

export default {
  setup() {
    const title = ref('臨時作業情報詳細');

    watchEffect(() => {
      document.title = title.value;
    });

    const temporaryWork = ref({});
    const route = useRoute();
    const temporaryWorkId = route.params.id;
    const year = route.query.year ? parseInt(route.query.year, 10) : '';
    const month = route.query.month ? parseInt(route.query.month, 10) : '';

    // ▼▼ 共通：検索条件をそのまま query として返す ▼▼
    const getSearchQuery = () => {
      const q = route.query;
      return {
        temp_billing_no: q.temp_billing_no || '',
        temp_year_from: q.temp_year_from || '',
        temp_month_from: q.temp_month_from || '',
        temp_year_to: q.temp_year_to || '',
        temp_month_to: q.temp_month_to || '',
        temp_customer_id: q.temp_customer_id || '',
        temp_building_id: q.temp_building_id || '',
        temp_vendor_id: q.temp_vendor_id || '',
        temp_user_id: q.temp_user_id || '',
        temp_work_content: q.temp_work_content || '',
        temp_billing_flg: q.temp_billing_flg || '',
        temp_order_sort_order: q.temp_order_sort_order || 'asc',
        temp_work_sort_order: q.temp_work_sort_order || '',
        temp_page: q.temp_page || 1
      };
    };

    const fetchTemporaryWork = async () => {
      const response = await getTemporaryWork(temporaryWorkId);
      temporaryWork.value = response.data;
    };

    const formatYearMonthDay = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    };

    const formatMonth = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getMonth() + 1}月`;
    };

    // Format currency
    const formatCurrency = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '¥0';
    };

    onMounted(fetchTemporaryWork);

    return {
      title,
      temporaryWork,
      year,
      month,
      route,
      getSearchQuery,
      formatYearMonthDay,
      formatMonth,
      formatCurrency
    };
  }
};
</script>

<style scoped>
.detail-container {
  width: 70%;
  padding: 20px;
  margin: auto;
  border: 1px solid #ddd;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.detail-item {
  margin-bottom: 10px;
}

table th {
  background: #2c3e50;
  color: #fff;
}

table th,
table td{
  padding: 8px;
}

.vendor-info-table th{
  color: #000;
  background-color: #e8eef8;
  font-weight: normal;
}

.btn-area{
  text-align: center;
}
</style>
